import { DATE_PICKER_FORMAT_MOMENT } from "definitions/constants";
import { TimezoneType } from "models/automation";
import moment, { Moment } from "moment";
import "moment-timezone";

export const DEFAULT_TIME_FORMAT = "h:mma";
const DEFAULT_TIME_FORMAT_WITH_TIMEZONE = `${DEFAULT_TIME_FORMAT} z`;

/**
 * Get the day of week from a date string in a specific timezone
 * @param dateString - The date string (ISO format or datetime-local format)
 * @param timezone - The IANA timezone (e.g., "America/New_York")
 * @returns The day of week in uppercase (e.g., "MO", "TU", "WE")
 */
export const getDayOfWeek = (dateString: string, timezone: string): string => {
  const dayNames = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
  const dayIndex = moment.tz(dateString, timezone).day();
  return dayNames[dayIndex];
};

/**
 * Get the day of week name for display in a specific timezone
 * @param dateString - The date string (ISO format or datetime-local format)
 * @param timezone - The IANA timezone (e.g., "America/New_York")
 * @returns The full day name (e.g., "Monday", "Tuesday")
 */
export const getDayName = (dateString: string, timezone: string): string => {
  return moment.tz(dateString, timezone).format("dddd");
};

/**
 * Calculate which occurrence of a weekday this is in the month (1st, 2nd, 3rd, 4th)
 * in a specific timezone
 * @param dateString - The date string (ISO format or datetime-local format)
 * @param timezone - The IANA timezone (e.g., "America/New_York")
 * @returns The occurrence number (1-4) and ordinal suffix ("st", "nd", "rd", "th")
 */
export const getNthWeekdayOfMonth = (dateString: string, timezone: string): { nth: number; ordinal: string } => {
  const dayOfMonth = moment.tz(dateString, timezone).date();
  const nth = Math.ceil(dayOfMonth / 7);

  const ordinalSuffix = (n: number): string => {
    if (n === 1) return "st";
    if (n === 2) return "nd";
    if (n === 3) return "rd";
    return "th";
  };

  return { nth, ordinal: ordinalSuffix(nth) };
};

export function dateInFormat(date: Date | string | undefined, format: string, timezone?: string): string {
  if (!date) return "";
  const momentDate = moment(date);
  if (timezone) {
    return momentDate.tz(timezone).format(format);
  }
  return momentDate.format(format);
}

export function display12hrTimeOfDay(timeValue: string | undefined) {
  return timeValue ? moment(timeValue, "HH:mm").format(DEFAULT_TIME_FORMAT) : "";
}

export function convertTime(timeString: string) {
  // Converts 24-hour time (e.g., "09:00", "14:00") to 12-hour format with AM/PM
  return moment(timeString, "HH:mm").format("h:mma");
}

export function getLocalTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function formattedTimezone(
  timezone: string | undefined | null,
  timezoneName: "short" | "long" | "shortGeneric" | "longGeneric" = "long",
) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone ?? undefined,
    timeZoneName: timezoneName,
  });
  const parts = formatter.formatToParts(new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value;
}

/**
 * Returns the UTC offset of a timezone in fractional hours (e.g. 5.5 for UTC+5:30).
 * Uses "shortOffset" formatToParts so half-hour timezones (India, Nepal, etc.) are exact.
 */
export function getTimezoneOffsetHours(tz: string): number {
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT+0";
    const m = offset.match(/GMT([+-])(\d+)(?::(\d+))?/);
    if (!m) return 0;
    return (parseInt(m[2]) + parseInt(m[3] ?? "0") / 60) * (m[1] === "+" ? 1 : -1);
  } catch {
    return 0;
  }
}

/**
 * Returns the hour difference between personTz and the browser's local timezone.
 * Positive = person is ahead, negative = person is behind.
 * Returns null if either timezone is unresolvable.
 */
export function getTimezoneDiffHours(personTz: string): number | null {
  try {
    const localTz = getLocalTimezone();
    return getTimezoneOffsetHours(personTz) - getTimezoneOffsetHours(localTz);
  } catch {
    return null;
  }
}

function getTimezoneAbbreviation(momentDate: moment.Moment, timezone: string): string {
  // First try to get the moment.js abbreviation
  const momentAbbrev = momentDate.format("z");

  // If it's not just a numeric offset, use it
  if (momentAbbrev && !momentAbbrev.match(/^[+-]\d/)) {
    return momentAbbrev;
  }

  // Otherwise, try to get a better abbreviation using Intl API
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(momentDate.toDate());
    const timezoneNamePart = parts.find((p) => p.type === "timeZoneName");

    if (timezoneNamePart?.value && timezoneNamePart.value !== timezone) {
      return timezoneNamePart.value;
    }
  } catch {
    // If Intl fails, fall back to moment abbreviation
  }

  // Final fallback: use the moment abbreviation (might be numeric offset)
  return momentAbbrev;
}

// Will display date/time like: "Tuesday, Jan 3"
export function taskDateDisplay(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "dddd, MMM D", timezone);
}

// Will display date/time like: "12:17 am on Tues, Jan 3"
export function tableTaskCompletedDateDisplay(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "hh:mma [on] ddd, MMM D", timezone);
}

// Will display date/time like: "Tuesday, January 3rd"
export function dateToLongString(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "dddd, MMMM Do", timezone);
}

// Will display date/time like: "Tues, Jan 3rd"
export function dateToAbbrevString(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "ddd, MMM Do", timezone);
}

// Will display date/time like: "Tues, 1/3""
export function dateToShortString(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "ddd, M/D", timezone);
}

// Will display date/time like: "Tuesday, January 3rd, 2024"
export function dateToFormalLongString(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "dddd, MMMM Do, YYYY", timezone);
}

// Will display date/time like: "Tuesday, January 3rd, 2024 at 12:47 am UTC"
export function dateToFormalLongStringWithTimeAndZone(value: Date | undefined) {
  return value
    ? moment(value).tz(getLocalTimezone()).format(`dddd, MMMM Do [at] ${DEFAULT_TIME_FORMAT_WITH_TIMEZONE}`)
    : "";
}

export function display12hrTimeOfDayWithTZ(timeValue: string | undefined) {
  return timeValue ? moment(timeValue, "HH:mm").tz(getLocalTimezone()).format(DEFAULT_TIME_FORMAT_WITH_TIMEZONE) : "";
}

export function dateTo12hrTimeOfDayWithTZInZone(dateTimeValue: string | undefined, timezone: string) {
  if (!dateTimeValue) return "";

  const momentDate = moment(dateTimeValue).tz(timezone);
  const timeStr = momentDate.format(DEFAULT_TIME_FORMAT);
  const timezoneAbbrev = getTimezoneAbbreviation(momentDate, timezone);

  return `${timeStr} ${timezoneAbbrev}`;
}

// Will display date/time like: "Jan 3rd, 2024"
export function dateToAbbrevMonthYear(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "MMM Do, YYYY", timezone);
}

// Will display date/time like: "Tuesday, January 3rd, 2024"
export function dateToAbbrevStringWithYear(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "ddd, MMM Do, YYYY", timezone);
}

// Will display date/time like: "12:47 am"
export function dateToTime(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, DEFAULT_TIME_FORMAT, timezone);
}

// Will display date/time like: "Tuesday, January 3rd at 12:47 am"
export function dateToLongStringWithTime(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, `dddd, MMMM Do [at] ${DEFAULT_TIME_FORMAT}`, timezone);
}

export function dateToLongStringWithTimeAndZone(value: Date | string | undefined, timezone: string) {
  // Will display date/time like: "Tuesday, January 3rd at 12:47 am EST"
  if (!value) return "";
  if (!timezone) return "";

  const momentDate = moment(value).tz(timezone);
  const dateTimeStr = momentDate.format(`dddd, MMMM Do [at] ${DEFAULT_TIME_FORMAT}`);
  const timezoneAbbrev = getTimezoneAbbreviation(momentDate, timezone);

  return `${dateTimeStr} ${timezoneAbbrev}`;
}

// Will display date/time like:  January 1
export function basicDayMonth(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "MMMM D", timezone);
}

// Will display date/time like:  01/03/2023
export function basicDate(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "L", timezone);
}

// Will display date/time like:  01/03/23
export function basicShortDate(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "MM/DD/YY", timezone);
}

// Will display date/time like: "Tues, 9/22 at 12:47 pm or Tomorrow at 12:47 pm"
export function timeAtSpecificDayMonth(value: Date | string | undefined) {
  if (!value) {
    return "";
  }
  if (moment(value).isBetween(moment().subtract(1, "day").startOf("day"), moment().add(1, "day").endOf("day"))) {
    return moment(value).calendar().replace("AM", "am").replace("PM", "pm");
  }
  return value ? moment(value).format(`ddd, MMM D [at] ${DEFAULT_TIME_FORMAT}`) : "";
}

// Will display date/time like: "9/22 at 12:47 pm or Yesterday at 12:47 pm"
export function timeAtDayMonth(value: Date | string | undefined) {
  if (!value) {
    return "";
  }
  if (moment(value).isBetween(moment().subtract(1, "day").startOf("day"), moment().add(1, "day").endOf("day"))) {
    return moment(value).calendar().replace("AM", "am").replace("PM", "pm");
  }
  return value ? moment(value).format(`MMM D [at] ${DEFAULT_TIME_FORMAT}`) : "";
}

// Will display date/time like: "12:47 PM on 9/22/2023"
export function timeOnUSDate(value: Date | string | undefined, timezone?: string) {
  return dateInFormat(value, "hh:mm a [on] l", timezone);
}

export function timeOnUSDateWithTimezone(value: Date | undefined, timezone: string) {
  // Will display date/time like: "12:47 PM on 9/22/2023 (PDT)"
  if (!value) return "";

  const momentDate = moment(value).tz(timezone);
  const timeAndDate = momentDate.format(`hh:mma [on] l`);
  const timezoneAbbrev = getTimezoneAbbreviation(momentDate, timezone);

  return `${timeAndDate} (${timezoneAbbrev})`;
}

export function lastUpdated(value: Date | undefined) {
  return value ? moment(value).fromNow() : "";
}

/**
 * Compact relative duration for list/table contexts: "2d", "3w", "1mo", "2y".
 * - Under 24 hours → "h" (with a "<1h" floor so we never show "0h")
 * - 1–6 days → "Nd"
 * - 7–29 days → "Nw"
 * - 30 days–11 months → "Nmo"
 * - 1 year+ → "Ny"
 * Always present tense — "ago" lives in a tooltip, not the cell.
 */
export function shortRelative(value: Date | string | null | undefined): string {
  if (!value) return "";
  const target = moment(value);
  if (!target.isValid()) return "";

  const now = moment();
  const seconds = Math.max(0, now.diff(target, "seconds"));

  const hour = 3600;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  if (seconds < hour) return "<1h";
  if (seconds < day) return `${Math.floor(seconds / hour)}h`;
  if (seconds < week) return `${Math.floor(seconds / day)}d`;
  if (seconds < month) return `${Math.floor(seconds / week)}w`;
  if (seconds < year) return `${Math.floor(seconds / month)}mo`;
  return `${Math.floor(seconds / year)}y`;
}

export function backendTimeFormat(value: Date | undefined) {
  return value ? moment(value).toISOString(true) : "";
}

export function setTimeFromOriginalDateTime(newDate: Date, originalDate: Date | string | undefined): Date {
  if (!originalDate) return newDate;
  const referenceDateObj = typeof originalDate === "string" ? new Date(originalDate) : originalDate;
  newDate.setHours(referenceDateObj.getHours());
  newDate.setMinutes(referenceDateObj.getMinutes());
  newDate.setSeconds(referenceDateObj.getSeconds());
  return newDate;
}

function timeFormat({ includeTimezone }: { includeTimezone: boolean }): string {
  return `${DEFAULT_TIME_FORMAT}${includeTimezone ? " z" : ""}`;
}

export function relativeDateTime(
  value: Date | undefined,
  options: { includeTimezone: boolean } = { includeTimezone: false },
): string {
  // Will display date/time like:
  // "Fri, Sep 23 at 12:47 pm"
  // "Last Friday at 12:47 pm"
  // "This Past Monday at 12:47 pm"
  // "Yesterday at 12:47 pm"
  // "Today at 12:47 pm"
  // "Tomorrow at 12:47 pm"
  // "This Friday at 12:47 pm"
  // "Next Monday at 12:47 pm"
  // "Fri, Sep 23 at 12:47 pm"
  if (!value) {
    return "";
  }

  const now = moment();
  let targetDate = moment(value);
  const daysDiff = targetDate.diff(now, "days");
  const format = timeFormat(options);
  if (options?.includeTimezone) {
    targetDate = targetDate.tz(getLocalTimezone());
  }

  // More than 1 week ago
  if (targetDate.isBefore(now.clone().subtract(1, "week").startOf("day"))) {
    return targetDate.format(`ddd MMM D [at] ${format}`);
  }

  // Within past week
  if (targetDate.isBetween(now.clone().subtract(1, "week").startOf("day"), now.startOf("day"))) {
    return targetDate.calendar(null, {
      sameDay: `[Today at] ${format}`,
      nextDay: `[Tomorrow at] ${format}`,
      nextWeek: `[This] dddd [at] ${format}`,
      lastDay: `[Yesterday at] ${format}`,
      lastWeek: `[Last] dddd [at] ${format}`,
      sameElse: `ddd MMM D [at] ${format}`,
    });
  }

  // Yesterday
  if (
    targetDate.isBetween(now.clone().subtract(1, "day").startOf("day"), now.clone().subtract(1, "day").endOf("day"))
  ) {
    return targetDate.format(`[Yesterday at] ${format}`);
  }

  // Today
  if (targetDate.isBetween(now.startOf("day"), now.endOf("day"))) {
    return targetDate.format(`[Today at] ${format}`);
  }

  // Tomorrow
  if (targetDate.isBetween(now.clone().add(1, "day").startOf("day"), now.clone().add(1, "day").endOf("day"))) {
    return targetDate.format(`[Tomorrow at] ${format}`);
  }

  // This week (from today until the end of this week)
  if (targetDate.isBetween(now, now.clone().endOf("week"))) {
    return targetDate.calendar(null, {
      sameDay: `[Today at] ${format}`,
      nextDay: `[Tomorrow at] ${format}`,
      nextWeek: `[This] dddd [at] ${format}`,
      lastDay: `[Yesterday at] ${format}`,
      lastWeek: `[Last] dddd [at] ${format}`,
      sameElse: `ddd MMM D [at] ${format}`,
    });
  }

  // Within the next week (but not this week)
  if (daysDiff >= 1 && daysDiff <= 7) {
    return targetDate.calendar(null, {
      sameDay: `[Today at] ${format}`,
      nextDay: `[Tomorrow at] ${format}`,
      nextWeek: `[Next] dddd [at] ${format}`,
      lastDay: `[Yesterday at] ${format}`,
      lastWeek: `[Last] dddd [at] ${format}`,
      sameElse: `ddd MMM D [at] ${format}`,
    });
  }

  // More than 1 week in the future
  return targetDate.format(`ddd MMM D [at] ${format}`);
}

export function relativeDateTimeWithTimezone(value: Date | undefined): string {
  return relativeDateTime(value, { includeTimezone: true });
}

export function relativeDate(value: Date | undefined): string {
  // Will display date/time like:
  // "Fri, Sep 23, 2024"
  // "Last Friday"
  // "This Past Monday"
  // "Yesterday"
  // "Today"
  // "Tomorrow"
  // "This Friday"
  // "Next Monday"
  // "Fri, Sep 23, 2024"
  if (!value) {
    return "";
  }

  const now = moment();
  const targetDate = moment(value);
  const daysDiff = targetDate.diff(now, "days");

  // More than 1 week ago
  if (targetDate.isBefore(now.clone().subtract(1, "week").startOf("day"))) {
    return targetDate.format("ddd MMM D, YYYY");
  }

  // Within past week
  if (targetDate.isBetween(now.clone().subtract(1, "week").startOf("day"), now.startOf("day"))) {
    return targetDate.calendar(null, {
      sameDay: "[Today]",
      nextDay: "[Tomorrow]",
      nextWeek: "[This] dddd",
      lastDay: "[Yesterday]",
      lastWeek: "[Last] dddd",
      sameElse: "ddd MMM D, YYYY",
    });
  }

  // Yesterday
  if (
    targetDate.isBetween(now.clone().subtract(1, "day").startOf("day"), now.clone().subtract(1, "day").endOf("day"))
  ) {
    return targetDate.format("[Yesterday]");
  }

  // Today
  if (targetDate.isBetween(now.startOf("day"), now.endOf("day"))) {
    return targetDate.format("[Today]");
  }

  // Tomorrow
  if (targetDate.isBetween(now.clone().add(1, "day").startOf("day"), now.clone().add(1, "day").endOf("day"))) {
    return targetDate.format("[Tomorrow]");
  }

  // This week (from today until the end of this week)
  if (targetDate.isBetween(now, now.clone().endOf("week"))) {
    return targetDate.calendar(null, {
      sameDay: "[Today]",
      nextDay: "[Tomorrow]",
      nextWeek: "[This] dddd",
      lastDay: "[Yesterday]",
      lastWeek: "[Last] dddd",
      sameElse: "ddd MMM D, YYYY",
    });
  }

  // Within the next week (but not this week)
  if (daysDiff >= 1 && daysDiff <= 7) {
    return targetDate.calendar(null, {
      sameDay: "[Today]",
      nextDay: "[Tomorrow]",
      nextWeek: "[Next] dddd",
      lastDay: "[Yesterday]",
      lastWeek: "[Last] dddd",
      sameElse: "ddd MMM D, YYYY",
    });
  }

  // More than 1 week in the future
  return targetDate.format("ddd MMM D, YYYY");
}

export function relativeShortDate(value: Date | undefined): string {
  // Will display date/time like:
  // "9-23-2024"
  // "Last Friday"
  // "This Past Monday"
  // "Yesterday"
  // "Today"
  // "Tomorrow"
  // "This Friday"
  // "Next Monday"
  // "9-23-2024"
  if (!value) {
    return "";
  }

  const now = moment();
  const targetDate = moment(value);
  const daysDiff = targetDate.diff(now, "days");

  // More than 1 week ago
  if (targetDate.isBefore(now.clone().subtract(1, "week").startOf("day"))) {
    return targetDate.format("L");
  }

  // Within past week
  if (targetDate.isBetween(now.clone().subtract(1, "week").startOf("day"), now.startOf("day"))) {
    return targetDate.calendar(null, {
      sameDay: "[Today]",
      nextDay: "[Tomorrow]",
      nextWeek: "[This] dddd",
      lastDay: "[Yesterday]",
      lastWeek: "[Last] dddd",
      sameElse: "L",
    });
  }

  // Yesterday
  if (
    targetDate.isBetween(now.clone().subtract(1, "day").startOf("day"), now.clone().subtract(1, "day").endOf("day"))
  ) {
    return targetDate.format("[Yesterday]");
  }

  // Today
  if (targetDate.isBetween(now.startOf("day"), now.endOf("day"))) {
    return targetDate.format("[Today]");
  }

  // Tomorrow
  if (targetDate.isBetween(now.clone().add(1, "day").startOf("day"), now.clone().add(1, "day").endOf("day"))) {
    return targetDate.format("[Tomorrow]");
  }

  // This week (from today until the end of this week)
  if (targetDate.isBetween(now, now.clone().endOf("week"))) {
    return targetDate.calendar(null, {
      sameDay: "[Today]",
      nextDay: "[Tomorrow]",
      nextWeek: "[This] dddd",
      lastDay: "[Yesterday]",
      lastWeek: "[Last] dddd",
      sameElse: "L",
    });
  }

  // Within the next week (but not this week)
  if (daysDiff >= 1 && daysDiff <= 7) {
    return targetDate.calendar(null, {
      sameDay: "[Today]",
      nextDay: "[Tomorrow]",
      nextWeek: "[Next] dddd",
      lastDay: "[Yesterday]",
      lastWeek: "[Last] dddd",
      sameElse: "L",
    });
  }

  // More than 1 week in the future
  return targetDate.format("L");
}

export function isInPast(value: Date | string | undefined): boolean {
  if (!value || !moment(value).isValid()) {
    return false;
  }
  return moment(value).isBefore(moment());
}

export function isWithinNext3Days(value: Date | undefined): boolean {
  const threeDaysOutEOD = moment().add(3, "days").endOf("day");
  const input = moment(value);

  return input.isSameOrBefore(threeDaysOutEOD, "second");
}

export function isWeekendDay(value: Date | undefined | Moment): boolean {
  const input = moment.isMoment(value) ? value : moment(value);

  return input.day() === 0 || input.day() === 6;
}

export function convertToLocalTimeString(utcDatetime: string | Date, timezone: string) {
  return moment.utc(utcDatetime).tz(timezone).format("HH:mm");
}

export function convertToTimezoneUSATimeString(utcDatetime: string | Date, timezone: string) {
  return moment.utc(utcDatetime).tz(timezone).format("h:mm A"); // e.g., "1:30 PM"
}

export function convertToLocalDateString(utcDatetime: string | Date, timezone: string) {
  return moment.utc(utcDatetime).tz(timezone).format(DATE_PICKER_FORMAT_MOMENT);
}

export function convertToLocalDate(utcDatetime: string | Date, timezone: string) {
  const localDateStr = convertToLocalDateString(utcDatetime, timezone);
  return new Date(`${localDateStr}T00:00:00`);
}

export function convertDateTo12pm(date: Date) {
  return moment(date).set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toDate();
}

export function buildISODate(date: string | Date, time: string, timezone: string) {
  let dateString = date;
  if (date instanceof Date) {
    dateString = moment(date).format(DATE_PICKER_FORMAT_MOMENT);
  }
  const combinedDateTime = `${dateString}T${time}`;
  const zonedDatetime = moment.tz(combinedDateTime, timezone);
  return zonedDatetime.toISOString();
}

// Tue, 6/18
export function meetingDay(value: Date | string | undefined, timezone?: string): string {
  return dateInFormat(value, "ddd, M/D", timezone);
}

export function meetingStartEnd(start: Date | string | undefined, end: Date | string | undefined, timezone?: string) {
  const formatTime = (inputTime: Date | string | undefined) => {
    const [time, meridem] = dateInFormat(inputTime, "h:mm a", timezone).split(" ");
    if (time.endsWith(":00")) {
      return [time.slice(0, -3), meridem];
    }
    return [time, meridem];
  };
  const [startTime, startMeridiem] = formatTime(start);
  const [endTime, endMeridiem] = formatTime(end);
  if (startMeridiem === endMeridiem) {
    return `${startTime}-${endTime}${endMeridiem}`;
  }
  return `${startTime}${startMeridiem}-${endTime}${endMeridiem}`;
}

export function dayDifference(startDate: Date | string | undefined, comparedDate: Date | string | undefined) {
  // this will give the date difference but using the compared date timezone for the start date
  if (!startDate || !comparedDate) {
    return null;
  }

  const compared = moment(comparedDate);
  const offsetMinutes = compared.utcOffset();

  const start = moment(startDate).utcOffset(offsetMinutes).startOf("day");
  compared.startOf("day");

  return compared.diff(start, "days");
}

export function dayDifferenceLabel(startDate: Date | string | undefined, comparedDate: Date | string | undefined) {
  // will return Day 1 (for same date), Day 2 or Day -1 (for day before start date)
  const differenceInDays = dayDifference(startDate, comparedDate);
  if (differenceInDays === null || differenceInDays === undefined) {
    return "";
  }
  return differenceInDays >= 0 ? `Day ${differenceInDays + 1}` : `Day ${differenceInDays}`;
}

export function formattedDayTimeWithTimezone(dateTime: Date | string, timezone = "America/Los_Angeles") {
  // Friday, July 20th at 12:00pm (PDT)
  const m = moment.tz(dateTime, timezone);
  const formatted = m.format("dddd, MMMM Do [at] h:mma");
  const abbreviation = getTimezoneAbbreviation(m, timezone);
  return `${formatted} (${abbreviation})`;
}

export function moveDateToNewDay(oldDate: Date, newDate: Date | string, timezone: string): Date {
  const oldInTz = moment.tz(oldDate, timezone);
  const newInTz = moment.tz(newDate, timezone);

  oldInTz.year(newInTz.year());
  oldInTz.month(newInTz.month());
  oldInTz.date(newInTz.date());

  return oldInTz.toDate();
}

export function dateToSimpleDateString(dateString: string | undefined) {
  if (!dateString) return dateString;
  const momentObject = moment(dateString);
  return momentObject.format("YYYY-MM-DD");
}

export function convertToCalendarTime(utcDatetime: string | Date, timezone: string, includeAmPm: boolean = true) {
  const hourTimeFormat = includeAmPm ? "h:mma" : "h:mm";
  return moment.utc(utcDatetime).tz(timezone).format(hourTimeFormat); // e.g., "1:30pm" or "1:30"
}

/**
 * Returns a human-friendly onboarding progress label relative to a start date.
 * - Before start: "Starts in X days" / "Starts tomorrow"
 * - Days 1–5: "Day 1" through "Day 5"
 * - Days 6–28: "Week 1" through "Week 4"
 * - Day 29+: "Month 2", "Month 3", etc. (uses 30-day month approximation)
 */
const MAX_DAY_DISPLAY = 4; // Days 0-4 → "Day 1" through "Day 5"
const MAX_WEEK_DISPLAY = 27; // Days 5-27 → "Week 1" through "Week 4"
const DAYS_PER_MONTH = 30; // Approximate; "Month 2" appears around day 30, not calendar month

export function onboardingDayLabel(startDate: Date | string | undefined): string {
  if (!startDate) return "";

  const now = moment().startOf("day");
  const start = moment(startDate).startOf("day");
  const daysSinceStart = now.diff(start, "days");

  if (daysSinceStart < 0) {
    const daysUntil = Math.abs(daysSinceStart);
    if (daysUntil === 1) return "Starts tomorrow";
    return `Starts in ${daysUntil} days`;
  }

  if (daysSinceStart <= MAX_DAY_DISPLAY) {
    return `Day ${daysSinceStart + 1}`;
  }

  if (daysSinceStart <= MAX_WEEK_DISPLAY) {
    const week = Math.floor(daysSinceStart / 7) + 1;
    return `Week ${week}`;
  }

  const month = Math.max(Math.floor(daysSinceStart / DAYS_PER_MONTH) + 1, 2);
  return `Month ${month}`;
}

const WEEK_PERIOD_THRESHOLD_DAYS = 84;

/** Like onboardingDayLabel, but always shows the day number alongside the week/month bucket.
 *  Examples: "Day 22 · Week 4", "Day 142 · Month 5". Pre-start dates fall back to onboardingDayLabel.
 */
export function dayAndPeriodLabel(startDate: Date | string | undefined): string {
  if (!startDate) return "";

  const now = moment().startOf("day");
  const start = moment(startDate).startOf("day");
  const daysSinceStart = now.diff(start, "days");

  if (daysSinceStart < 0) return onboardingDayLabel(startDate);

  const dayLabel = `Day ${daysSinceStart + 1}`;

  if (daysSinceStart < WEEK_PERIOD_THRESHOLD_DAYS) {
    const week = Math.floor(daysSinceStart / 7) + 1;
    return `${dayLabel} · Week ${week}`;
  }

  const month = Math.max(Math.floor(daysSinceStart / DAYS_PER_MONTH) + 1, 2);
  return `${dayLabel} · Month ${month}`;
}

export function relativeDueDate(dueAt: Date | string | undefined | null, timezone: TimezoneType) {
  if (!dueAt) return "";
  const now = moment().tz(timezone);
  const targetDate = moment(dueAt).tz(timezone);
  const diffInDays = targetDate.diff(now, "days");

  if (diffInDays === -1) return "Yesterday";
  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Tomorrow";

  // Past dates (more than 1 day ago)
  if (diffInDays < -1) {
    return `${Math.abs(diffInDays)} days ago`;
  }

  // Future dates within 1 week
  if (diffInDays <= 7) {
    return `In ${diffInDays} days`;
  }

  // More than 1 week away
  return targetDate.format("ddd, MMM, D");
}

// Parses YYYY-MM-DD date strings as local midnight (not UTC) to avoid off-by-one day shifts.
export function parseDateOnly(d: Date | string): Date {
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-").map(Number);
  return new Date(y, m - 1, day);
}
